import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { useShareBySlug } from "@/hooks/useNotebookShares";
import { useForceLightMode } from "@/hooks/useTheme";

export const Route = createFileRoute("/share/$slug")({
  component: SharedNoteRoute,
});

/**
 * Public, read-only view of a published notebook note. No auth, no owner
 * identity — just the snapshot behind an unguessable slug. Kept noindex:
 * "anyone with the link" means people, not search engines.
 */
function SharedNoteRoute() {
  useForceLightMode();
  const { slug } = Route.useParams();
  const share = useShareBySlug(slug);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-home-cream font-body text-home-ink">
      <header className="border-b border-home-ink/10 px-5 py-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link to="/" className="font-heading text-[17px] font-bold text-home-ink">
            Clawcraft
          </Link>
          <span className="rounded-full bg-home-ink/5 px-3 py-1 text-[11px] font-bold tracking-wide text-home-ink/50 uppercase">
            Shared note
          </span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-10 md:px-6 md:py-14">
        {share === undefined ? (
          <p className="text-home-ink/40">Loading…</p>
        ) : share === null ? (
          <div className="py-20 text-center">
            <h1 className="font-heading text-2xl font-bold">This note isn't available.</h1>
            <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-home-ink/55">
              The link may be wrong, or the author took the note down.
            </p>
            <Link
              to="/"
              className="mt-8 inline-block rounded-full bg-home-yellow px-6 py-2.5 text-sm font-bold text-home-ink"
            >
              Go to Clawcraft
            </Link>
          </div>
        ) : (
          <article>
            <h1 className="font-heading text-3xl font-bold">{share.title}</h1>
            <p className="mt-2 text-[13px] text-home-ink/40">
              Published {new Date(share.publishedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <div className="prose prose-base mt-8 max-w-none">
              <MarkdownContent content={share.content} />
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-home-ink/10 px-5 py-6 text-center">
        <p className="text-[13px] text-home-ink/45">
          Written in a{" "}
          <Link to="/" className="font-semibold text-home-oxblood hover:underline">
            Clawcraft
          </Link>{" "}
          notebook — where an agent does the work and shows its sources.
        </p>
      </footer>
    </div>
  );
}
